import React, { useState } from "react";
import { Block, Edit, OpenWith } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";

import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import { getCurrentProject } from "fond/project/redux";
import { useAppSelector } from "fond/utils/hooks";
import { Actions } from "fond/utils/permissions";
import { isExtendedPlannerProject } from "fond/utils/project";
import { ConfirmModal } from "fond/widgets";
import { LockIcon } from "fond/widgets/Navigation/navigation.styles";

import { useDesignRefinementActions } from "../hooks/useDesignRefinementActions";

import RefinementButton from "./RefinementButton";

interface SolveRefinementsProps {
  hasMovedHubs: boolean;
  hasDropAssignments: boolean;
  hasBlockers: boolean;
}

/**
 * Refinement controls for a completed design, each marked when it has been applied.
 */
const SolveRefinements: React.FC<SolveRefinementsProps> = ({ hasMovedHubs, hasDropAssignments, hasBlockers }) => {
  const project = useAppSelector((state) => getCurrentProject(state.project));
  const isAllowed = usePermissionCheck(Actions.PLANNER_PROJECT_EDIT, project?.Permission.Level);
  const { resetEdits } = useDesignRefinementActions();
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const hasEdits = hasMovedHubs || hasDropAssignments || hasBlockers;

  const onConfirmReset = () => {
    resetEdits();
    setShowResetConfirm(false);
  };

  return (
    <>
      {showResetConfirm && (
        <ConfirmModal
          open
          content="Are you sure you want to reset all design edits?"
          onConfirm={onConfirmReset}
          onCancel={() => setShowResetConfirm(false)}
        />
      )}
      <Box mt={2}>
        <Typography variant="h6" fontSize={14}>
          Make refinements
        </Typography>
        <Box display="flex" justifyContent="center" mt={0.5} mb={0.5}>
          {!isAllowed && <LockIcon />}
        </Box>
        <Typography variant="body3" textAlign={isAllowed ? undefined : "center"}>
          {isAllowed
            ? "The design has been successfully generated. To make modifications select an action below:"
            : "You need a license/subscription to make refinements."}
        </Typography>
        {isExtendedPlannerProject(project) && (
          <RefinementButton icon={<Block />} title="Blockers" target="extended-design-blockers" checked={hasBlockers} disabled={!isAllowed} />
        )}
        <RefinementButton icon={<OpenWith />} title="Hubs" target="design-hubs" checked={hasMovedHubs} disabled={!isAllowed} />
        {isExtendedPlannerProject(project) && (
          <RefinementButton
            icon={<Edit />}
            title="Drop assignments"
            target="design-drop-assignments"
            checked={hasDropAssignments}
            disabled={!isAllowed}
          />
        )}
        {hasEdits && isAllowed && (
          <Button fullWidth variant="outlined" color="error" size="small" sx={{ mt: 1 }} onClick={() => setShowResetConfirm(true)}>
            Reset edits
          </Button>
        )}
      </Box>
    </>
  );
};

export default SolveRefinements;
